import { useCallback } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import {
	useContributionsPageData,
	useContributionsPageState,
} from '#src/app/pages/accountSettings/contributions/main/pageContext';
import { convertToUsdNumber } from '#src/app/shared/utils/currency';

import { updateContributionAmount } from '../../../../../../../ModifyContributions/components/ChangeContributionForm/hooks/useChangeContributionsForm/hooks/useSubmit/api/updateContributionAmount';

export function useClickLowerByHalf(newAmount: number): () => Promise<void> {
	const { data } = useContributionsPageData();
	const [{ subscription: subs }, actions] = useContributionsPageState();
	const { translate, amountToBaseUnits } = useI18n();
	const utilityContext = useUtilityContext();
	const track = useTracking();

	const exchangeRate = data.currencyExchangeRate ?? 1;
	const amountInUsd = convertToUsdNumber(newAmount, exchangeRate, subs.currencyCode);

	return useCallback(async () => {
		void track('subs_cancel_modal_reduce_click');
		actions.resetPageMessage();
		const amountInBaseUnits = amountToBaseUnits(newAmount, subs.currencyCode);
		const response = await updateContributionAmount(
			{ amount: amountInBaseUnits, id: subs.id, currency: subs.currencyCode },
			utilityContext,
		);

		if (response.success) {
			actions.handleContributionsUpdateSuccess({
				amount: newAmount,
				message: translate('fe.pages.subscriptions.manage.plan_saved_success'),
			});
			actions.setContributionAmount(newAmount);
			void track('subs_settings_reduce_success', { local_amount: newAmount, amount: amountInUsd });
		} else {
			void track('subs_settings_reduce_failure');
			actions.setPageMessage({
				message: translate('fe.errors.generic_try_again'),
				severity: 'critical',
			});
		}
	}, [
		subs.id,
		track,
		actions,
		amountToBaseUnits,
		newAmount,
		subs.currencyCode,
		utilityContext,
		translate,
		amountInUsd,
	]);
}
